import * as i0 from '@angular/core';
import { Injectable, InjectionToken, inject, Renderer2, InjectFlags, RendererFactory2, TemplateRef, ApplicationRef, Injector, ComponentFactoryResolver, Inject } from '@angular/core';
import { coerceArray } from '@slidebean/hydra/coercion';

class TextContentRef {
    constructor(content) {
        this.content = content;
    }
    nodes() {
        return this.content;
    }
    destroy() { }
}
class TextContentRenderer {
    constructor(renderer) {
        this.renderer = renderer;
    }
    render(options) {
        return new TextContentRef([this.renderer.createText(options.value)]);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TextContentRenderer, deps: [{ token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TextContentRenderer }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TextContentRenderer, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: i0.Renderer2 }] });
function isTextContent(options) {
    return typeof options?.value === 'string';
}
const TEXT_CONTENT_RENDERER = new InjectionToken('@slidebean/hydra/dynamic/text-content-renderer', {
    factory() {
        let renderer = inject(Renderer2, InjectFlags.Optional);
        if (!renderer) {
            const factory = inject(RendererFactory2);
            renderer = factory.createRenderer(null, null);
        }
        return new TextContentRenderer(renderer);
    },
});
class TemplateContentRef {
    constructor(view, app) {
        this.view = view;
        this.app = app;
    }
    nodes() {
        return this.view.rootNodes;
    }
    destroy() {
        this.app.detachView(this.view);
        this.view.destroy();
    }
}
class TemplateContentRenderer {
    constructor(app) {
        this.app = app;
    }
    render(options) {
        const view = options.value.createEmbeddedView(options.context);
        view.detectChanges();
        this.app.attachView(view);
        return new TemplateContentRef(view, this.app);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TemplateContentRenderer, deps: [{ token: i0.ApplicationRef }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TemplateContentRenderer }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TemplateContentRenderer, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: i0.ApplicationRef }] });
function isTemplateContent(options) {
    return options?.value instanceof TemplateRef;
}
const TEMPLATE_CONTENT_RENDERER = new InjectionToken('@slidebean/hydra/dynamic/template-content-renderer', {
    factory() {
        return new TemplateContentRenderer(inject(ApplicationRef));
    },
});
class ComponentContentRef {
    constructor(component, app) {
        this.component = component;
        this.app = app;
    }
    nodes() {
        return [this.component.location.nativeElement];
    }
    destroy() {
        this.app.detachView(this.component.hostView);
        this.component.destroy();
    }
}
class ComponentContentRenderer {
    constructor(resolver, injector, app) {
        this.resolver = resolver;
        this.injector = injector;
        this.app = app;
    }
    render(options) {
        const factory = this.resolver.resolveComponentFactory(options.value);
        const component = factory.create(this.resolveInjector(options));
        this.app.attachView(component.hostView);
        return new ComponentContentRef(component, this.app);
    }
    resolveInjector(options) {
        const parent = options.injector ?? options?.container?.injector ?? this.injector;
        if (!options.providers || !options.providers.length)
            return parent;
        return Injector.create({
            providers: options.providers,
            parent,
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ComponentContentRenderer, deps: [{ token: i0.ComponentFactoryResolver }, { token: i0.Injector }, { token: i0.ApplicationRef }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ComponentContentRenderer }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ComponentContentRenderer, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: i0.ComponentFactoryResolver }, { type: i0.Injector }, { type: i0.ApplicationRef }] });
function isComponentContent(options) {
    return typeof options?.value === 'function';
}
const COMPONENT_CONTENT_RENDERER = new InjectionToken('@slidebean/hydra/dynamic/component-content-renderer', {
    factory() {
        return new ComponentContentRenderer(inject(ComponentFactoryResolver), inject(Injector), inject(ApplicationRef));
    },
});
class ElementContentRef {
    constructor(content) {
        this.content = content;
    }
    nodes() {
        return this.content;
    }
    destroy() { }
}
class EmptyContentRef {
    static { this.instance = new EmptyContentRef(); }
    constructor() { }
    nodes() {
        return [];
    }
    destroy() { }
}
class ContentRenderer {
    constructor(textRenderer, templateRenderer, componentRenderer) {
        this.textRenderer = textRenderer;
        this.templateRenderer = templateRenderer;
        this.componentRenderer = componentRenderer;
    }
    render(options) {
        if (!options?.value)
            return EmptyContentRef.instance;
        if (isTextContent(options)) {
            return this.textRenderer.render(options);
        }
        if (isTemplateContent(options)) {
            return this.templateRenderer.render(options);
        }
        if (isComponentContent(options)) {
            return this.componentRenderer.render(options);
        }
        return new ElementContentRef(coerceArray(options.value));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ContentRenderer, deps: [{ token: TEXT_CONTENT_RENDERER }, { token: TEMPLATE_CONTENT_RENDERER }, { token: COMPONENT_CONTENT_RENDERER }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ContentRenderer, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ContentRenderer, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [TEXT_CONTENT_RENDERER]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [TEMPLATE_CONTENT_RENDERER]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [COMPONENT_CONTENT_RENDERER]
                }] }] });

class AppAttachedDynamicComponentRef {
    constructor(app, component, content) {
        this.app = app;
        this.component = component;
        this.content = content;
    }
    get element() {
        return this.component.location.nativeElement;
    }
    destroy() {
        if (!!this.content)
            this.content.destroy();
        if (!!this.component) {
            this.app.detachView(this.component.hostView);
            this.component.destroy();
        }
    }
}
class ViewAttachedDynamicComponentRef {
    constructor(container, component, content) {
        this.container = container;
        this.component = component;
        this.content = content;
    }
    get element() {
        return this.component.location.nativeElement;
    }
    destroy() {
        if (!!this.content)
            this.content.destroy();
        if (!!this.component) {
            this.container.remove(this.container.indexOf(this.component.hostView));
            this.component.destroy();
        }
    }
}
class DynamicComponentFactory {
    constructor(app, injector, resolver, renderer) {
        this.app = app;
        this.injector = injector;
        this.resolver = resolver;
        this.renderer = renderer;
    }
    create(type, options) {
        if (!!options?.container) {
            return this.createViewAttached(type, options);
        }
        return this.createAppAttached(type, options ?? {});
    }
    createViewAttached(type, options) {
        const view = options.container;
        const injector = options.injector;
        const content = this.renderer.render(options.content);
        const component = view.createComponent(this.resolver.resolveComponentFactory(type), options.index ?? view.length, this.resolveInjector(injector ?? view.injector, options.providers), [content.nodes()]);
        return new ViewAttachedDynamicComponentRef(view, component, content);
    }
    createAppAttached(type, options) {
        const injector = options.injector;
        const content = this.renderer.render(options.content);
        const factory = this.resolver.resolveComponentFactory(type);
        const component = factory.create(this.resolveInjector(injector ?? this.injector, options.providers), [content.nodes()]);
        this.app.attachView(component.hostView);
        return new AppAttachedDynamicComponentRef(this.app, component, content);
    }
    resolveInjector(parent, providers = []) {
        if (!providers || !providers.length)
            return parent;
        return Injector.create({ providers, parent });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DynamicComponentFactory, deps: [{ token: i0.ApplicationRef }, { token: i0.Injector }, { token: i0.ComponentFactoryResolver }, { token: ContentRenderer }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DynamicComponentFactory, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DynamicComponentFactory, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: i0.ApplicationRef }, { type: i0.Injector }, { type: i0.ComponentFactoryResolver }, { type: ContentRenderer }] });

/**
 * Generated bundle index. Do not edit.
 */

export { AppAttachedDynamicComponentRef, COMPONENT_CONTENT_RENDERER, ComponentContentRef, ComponentContentRenderer, ContentRenderer, DynamicComponentFactory, ElementContentRef, EmptyContentRef, TEMPLATE_CONTENT_RENDERER, TEXT_CONTENT_RENDERER, TemplateContentRef, TemplateContentRenderer, TextContentRef, TextContentRenderer, ViewAttachedDynamicComponentRef };
